import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import preset from '../../tailwind.preset.cjs';

/**
 * De namen van onze eigen typografische schaal, rechtstreeks uit de preset.
 *
 * Uit de preset en niet overgetypt: een nieuwe maat toevoegen mag nooit betekenen dat je op twee
 * plaatsen moet denken. Vergeet je de tweede, dan werkt de klasse in de browser en gooit `cn()`
 * hem weg — en dat is precies het defect dat hieronder beschreven staat.
 */
const fontSizeNames = Object.keys(preset.theme.extend.fontSize);

/**
 * En dezelfde namen voor de ruimte: `card`, `element`, `inline`, `page-x`, …
 *
 * Deze lijst is er bij UI-7 gekomen, en om dezelfde fout als hierboven — één laag dieper. `p-0`
 * naast `p-card` op dezelfde kaart liet beide staan: tailwind-merge kent `p-card` niet als padding
 * en plaatst het in geen enkele groep, dus botste er niets en won er niets. De berichtkaart kreeg
 * 24 px van de kaartcomponent bóvenop de paddings per blok, en de scheidingslijn van de voet sprong
 * mee naar binnen — precies wat de gemeten test in `berichtkaart.spec.ts` rood maakte.
 */
const spacingNames = Object.keys(preset.theme.extend.spacing);

/**
 * Tailwind-merge, maar dan met onze schaal erin.
 *
 * **Waarom dit niet de kale `twMerge` kan zijn.** Tailwind-merge lost conflicten op door een klasse
 * in een groep te plaatsen: twee klassen uit dezelfde groep botsen, en de laatste wint. Welke
 * groep dat is, leidt het af uit de naam — en het kent alleen de standaardschaal (`text-sm`,
 * `text-lg`, …). Onze schaal heet anders (`text-micro`, `text-body`, `text-title-sm`, …), dus
 * `text-micro` wordt niet als grootte herkend maar als **tekstkleur**. Staat er in dezelfde
 * `cn()` een echte kleur, dan "wint" die van de grootte en verdwijnt de maat uit de DOM.
 *
 * Dat is geen theorie. In `AppTabBar` stond `font-heading text-micro font-medium` naast
 * `text-brand-primary`; de browser kreeg `font-heading font-medium`, het tablabel viel terug op de
 * 16 px uit de a11y-bodem, en in de onderbalk liepen "Documenten" en "Meldingen" over elkaar heen.
 * Hetzelfde gebeurde stilletjes in `Badge`, `Card`, `Input` en `Table`: de code las goed, de
 * browser deed iets anders, en niemand kon aanwijzen waarom de app er slordig uitzag.
 *
 * De test in `__tests__/cn.test.ts` legt dit vast. Die controleert de uitkomst van `cn()`, niet de
 * configuratie — een test op de configuratie zou dezelfde aanname herhalen die de fout maakte.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: fontSizeNames }],

      /*
        De ruimte-groepen, elk met de namen uit de preset erbij.

        Per groep en niet in één lijst, omdat `p-card` en `px-card` niet met elkaar botsen maar wel
        elk met hun eigen soort: wie `p-0 px-5` schrijft, wil beide. Tailwind-merge weet dat van zijn
        eigen maten al; het moest het alleen nog van de onze weten.
      */
      p: [{ p: spacingNames }],
      px: [{ px: spacingNames }],
      py: [{ py: spacingNames }],
      pt: [{ pt: spacingNames }],
      pr: [{ pr: spacingNames }],
      pb: [{ pb: spacingNames }],
      pl: [{ pl: spacingNames }],
      m: [{ m: spacingNames }],
      mx: [{ mx: spacingNames }],
      my: [{ my: spacingNames }],
      mt: [{ mt: spacingNames }],
      mr: [{ mr: spacingNames }],
      mb: [{ mb: spacingNames }],
      ml: [{ ml: spacingNames }],
      gap: [{ gap: spacingNames }],
      'gap-x': [{ 'gap-x': spacingNames }],
      'gap-y': [{ 'gap-y': spacingNames }],
    },
  },
});

/** Voegt classnames samen en laat latere Tailwind-classes winnen. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Alleen voor de test: de maten die `cn()` als grootte en als ruimte herkent. */
export const knownFontSizes = fontSizeNames;
export const knownSpacings = spacingNames;
